import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Path, Users, Coins, Mountains } from "@phosphor-icons/react";
import { HERO_MAP, track } from "../api";
import { brandBanner } from "../brand";
import Disclaimer from "../components/Disclaimer";
import InstallApp from "../components/InstallApp";

const features = [
  { icon: Path, title: "Strategic Decisions", text: "Five choices per expedition shape your fate. No single right answer." },
  { icon: Users, title: "Choose Your Crew", text: "Six specialists, each with strengths and flaws. Pick three wisely." },
  { icon: Coins, title: "Stake Virtual Gold", text: "Risk resources on your own skill, not on a spinning wheel." },
  { icon: Mountains, title: "Skill Challenge", text: "A steady hand at the crucial moment tips the odds in your favor." },
];

export default function Landing() {
  React.useEffect(() => { track("landing_viewed"); }, []);
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-ff-bg text-ff-parchment">
      {/* hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_MAP})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ff-bg/70 via-ff-bg/85 to-ff-bg" />
        <div className="animate-clouds pointer-events-none absolute -inset-x-40 top-10 h-40 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.06),transparent_70%)]" />

        <div className="relative mx-auto max-w-5xl px-5 pb-16 pt-16 sm:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-start"
          >
            <div className="mb-6 flex items-center gap-3">
              <Compass size={28} weight="duotone" className="animate-compass text-ff-gold drop-shadow-[0_0_10px_rgba(212,175,55,0.5)]" />
              <span className="font-mono-ff text-xs uppercase tracking-[0.3em] text-ff-brass">Expedition Guild</span>
            </div>

            <img
              src={brandBanner}
              alt="Aurumward"
              data-testid="hero-logo"
              className="w-full max-w-3xl rounded-lg object-contain shadow-[0_25px_60px_-20px_rgba(0,0,0,0.9)]"
            />
            <p className="mt-5 font-display text-2xl italic text-ff-muted sm:text-3xl">
              Risk the journey. Earn the legend.
            </p>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-ff-parchment/80">
              An exploration and strategy game where you stake virtual Gold on your own ability to
              complete an expedition. Choose your crew, weigh every decision, prove your skill, and
              claim your reward. This is calculated risk, not a game of chance.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                data-testid="play-demo-btn"
                onClick={() => nav("/login?demo=1")}
                className="rounded-sm bg-ff-gold px-8 py-3.5 font-semibold text-ff-bg transition-all duration-200 hover:bg-ff-brass hover:ff-glow-gold active:scale-95"
              >
                Play Demo
              </button>
              <button
                data-testid="create-account-btn"
                onClick={() => nav("/login?mode=register")}
                className="rounded-sm border border-ff-gold/60 bg-transparent px-8 py-3.5 font-semibold text-ff-gold transition-all duration-200 hover:bg-ff-gold/10 active:scale-95"
              >
                Create Account
              </button>
            </div>
            <div className="mt-4 sm:max-w-xs"><InstallApp /></div>
          </motion.div>
        </div>
      </section>

      {/* features */}
      <section className="mx-auto max-w-5xl px-5 py-14">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="ff-panel relative overflow-hidden rounded-sm p-6"
              data-testid={`feature-${i}`}
            >
              <f.icon size={30} weight="duotone" className="mb-3 text-ff-gold" />
              <h3 className="font-display text-xl font-semibold text-ff-parchment">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ff-muted">{f.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10">
          <Disclaimer />
        </div>
      </section>

      <footer className="border-t border-ff-brass/20 py-6 text-center text-xs text-ff-muted">
        Aurumward — a virtual expedition game. All Gold is in-game only.
        <span className="mx-2 text-ff-brass/40">·</span>
        <Link to="/seasons" data-testid="footer-seasons-link" className="text-ff-brass transition-colors hover:text-ff-gold">Seasonal Archives</Link>
        <span className="mx-2 text-ff-brass/40">·</span>
        <Link to="/hall-of-renown" data-testid="footer-hall-link" className="text-ff-brass transition-colors hover:text-ff-gold">Hall of Renown</Link>
        <span className="mx-2 text-ff-brass/40">·</span>
        <Link to="/sponsors" data-testid="footer-sponsors-link" className="text-ff-brass transition-colors hover:text-ff-gold">For Sponsors</Link>
      </footer>
    </div>
  );
}
